import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import Button from 'components/Button/Button';
import Checkbox from 'components/Checkbox/Checkbox';
import ScenarioShowcase from 'components/ScenarioShowcase/ScenarioShowcase';
import SmallLoader from 'components/SmallLoader/SmallLoader';
import TextField from 'components/TextField/TextField';
import { BUTTON_VARIANT } from 'constants/interfaces';

import {
  EXISTING_SCENARIO_CHECKBOX_NAME_PREFIX,
  EXISTING_SCENARIO_SCROLLABLE_TARGET,
  EXISTING_SCENARIO_SEARCH_FIELD_NAME,
} from './generals/existingScenarioGenerals';

import type IExistingScenario from './interfaces/IExistingScenario';

import styles from './scss/ExistingScenario.module.scss';

function ExistingScenario({
  scenarios,
  boardEntities,
  isLoading,
  hasMore,
  isDuplicateLoading,
  onLoadMore,
  onDuplicate,
}: IExistingScenario): React.JSX.Element {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selectedScenarioIds, setSelectedScenarioIds] = useState<Set<number>>(new Set());

  const filteredScenarios = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
      return scenarios;
    }

    return scenarios.filter((scenario) => (
      scenario.name.toLowerCase().includes(normalizedSearch)
    ));
  }, [scenarios, search]);

  const selectedCount = selectedScenarioIds.size;
  const hasSelectedScenarios = selectedCount > 0;
  const shouldShowLoader = isLoading && scenarios.length === 0;
  const shouldShowEmptyState = !isLoading && filteredScenarios.length === 0;

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleToggleScenario = (scenarioId: number) => {
    setSelectedScenarioIds((prevSelectedScenarioIds) => {
      const nextSelectedScenarioIds = new Set(prevSelectedScenarioIds);

      if (nextSelectedScenarioIds.has(scenarioId)) {
        nextSelectedScenarioIds.delete(scenarioId);
        return nextSelectedScenarioIds;
      }

      nextSelectedScenarioIds.add(scenarioId);
      return nextSelectedScenarioIds;
    });
  };

  const handleDuplicate = async () => {
    if (!hasSelectedScenarios) return;
    await onDuplicate(Array.from(selectedScenarioIds));
  };

  return (
    <>
      <div
        id={EXISTING_SCENARIO_SCROLLABLE_TARGET}
        className={styles.content}
      >
        <h2 className={styles.title}>
          {t('valueChainMap.copyExistingScenarios')}
        </h2>
        <TextField
          name={EXISTING_SCENARIO_SEARCH_FIELD_NAME}
          value={search}
          onChange={handleSearchChange}
          placeholder={t('common.search')}
          icon={<SearchIcon aria-hidden="true" />}
          className={styles.searchField}
        />
        {shouldShowLoader ? (
          <div className={styles.loaderWrapper} role="status" aria-live="polite">
            <SmallLoader size={30} />
          </div>
        ) : null}
        {shouldShowEmptyState ? (
          <div className={styles.emptyState}>
            <InfoIcon className={styles.emptyStateIcon} aria-hidden="true" />
            <p className={styles.emptyStateText}>
              {t('valueChainMap.noExistingScenarios')}
            </p>
          </div>
        ) : null}
        {filteredScenarios.length > 0 ? (
          <InfiniteScroll
            dataLength={filteredScenarios.length}
            next={onLoadMore}
            hasMore={hasMore}
            loader={null}
            scrollableTarget={EXISTING_SCENARIO_SCROLLABLE_TARGET}
          >
            <div className={styles.scenariosList}>
              {filteredScenarios.map((scenario) => {
                const isSelected = selectedScenarioIds.has(scenario.id);

                return (
                  <ScenarioShowcase
                    key={scenario.id}
                    scenario={scenario}
                    boardEntities={boardEntities}
                    isActive={isSelected}
                    hideActions
                    showEmptyInfoRows
                    stopEntitiesTogglePropagation
                    headerStartContent={(
                      <Checkbox
                        name={`${EXISTING_SCENARIO_CHECKBOX_NAME_PREFIX}-${scenario.id}`}
                        value={scenario.id}
                        checked={isSelected}
                        onChange={() => handleToggleScenario(scenario.id)}
                        label={(
                          <span className={styles.visuallyHidden}>
                            {t('valueChainMap.selectExistingScenario', { name: scenario.name })}
                          </span>
                        )}
                        inputAriaLabel={t('valueChainMap.selectExistingScenario', {
                          name: scenario.name,
                        })}
                        className={styles.checkbox}
                      />
                    )}
                    onSelect={() => handleToggleScenario(scenario.id)}
                  />
                );
              })}
            </div>
          </InfiniteScroll>
        ) : null}
      </div>

      <div className={styles.footer}>
        <Button
          title={t('common.duplicate')}
          variant={BUTTON_VARIANT.primary}
          className={styles.duplicateButton}
          disabled={!hasSelectedScenarios}
          isLoading={isDuplicateLoading}
          onClick={handleDuplicate}
        />
      </div>
    </>
  );
}

export default ExistingScenario;
